import { animated } from '@react-spring/web'
import { useCallback, useContext, useMemo } from 'react'

import { getItemTitle, toRecord } from '../../lib/helpers'
import { useBlinkingState } from '../../lib/preview-animation-hook'
import { type FrameBuilderGlass } from '../../lib/state'
import { getStatePrice, getStatePriceWithDiscount } from '../../lib/state-price'
import { type StepDefinitionProps } from '../../lib/step-definition'
import { FrameBuilderConfigurationContext } from '../../contexts/configuration-context'
import {
  FrameBuilderCommittedStateContext,
  FrameBuilderSelectedStateContext,
} from '../../contexts/state-context'
import { FrameBuilderStringsContext } from '../../contexts/strings-context'
import { FrameBuilderValidationContext } from '../../contexts/validation-context'

import Button from '../utility/button'
import ItemList from '../utility/item-list'
import ListContainer from '../utility/list-container'
import PortableText from '../utility/portable-text'
import PriceBadge from '../utility/price-badge'
import ConfigurationDefinition from './configuration-definition'

const GlassDefinition = ({
  number,
  onClose,
  className,
}: StepDefinitionProps) => {
  const { committedState } = useContext(FrameBuilderCommittedStateContext)
  const { selectedState, dispatchSelectedState } = useContext(
    FrameBuilderSelectedStateContext,
  )
  const { validationMessages } = useContext(FrameBuilderValidationContext)
  const frameBuilderStrings = useContext(FrameBuilderStringsContext)
  const configuration = useContext(FrameBuilderConfigurationContext)

  const [typeOpacity, setIsTypeBlinking] = useBlinkingState()

  // Calculate frame prices for comparison
  const framePrices = useMemo(
    () =>
      toRecord<number>(
        configuration.glass.map((glass) => ({
          [glass._id]: getStatePrice(
            {
              ...committedState,
              glass,
            },
            configuration,
          ),
        })),
      ),
    [committedState, configuration],
  )
  const framePricesWithDiscount = useMemo(
    () =>
      toRecord<number>(
        configuration.glass.map((glass) => ({
          [glass._id]: getStatePriceWithDiscount(
            {
              ...committedState,
              glass,
            },
            configuration,
          ),
        })),
      ),
    [committedState, configuration],
  )

  const selectGlass = useCallback(
    (glass: FrameBuilderGlass) => {
      dispatchSelectedState({
        type: 'glass',
        glass,
      })
    },
    [dispatchSelectedState],
  )
  const handleChange = useCallback(
    (id: string) => {
      const newGlass = configuration.glass.find((glass) => glass._id === id)

      if (newGlass) {
        selectGlass(newGlass)
      }
    },
    [configuration.glass, selectGlass],
  )

  // TODO: Wrong way to check for valid state!
  const isValidSelection = useMemo(
    () => !validationMessages?.glassTypePreview,
    [validationMessages],
  )

  const handleOkClick = useCallback(() => {
    if (isValidSelection) {
      onClose()
    }

    if (!isValidSelection) {
      setIsTypeBlinking(true)
    }
  }, [isValidSelection, setIsTypeBlinking, onClose])

  return (
    <ConfigurationDefinition
      number={number}
      onClose={onClose}
      title={frameBuilderStrings.glassStepTitle}
      className={className}
    >
      <div className="flex flex-col px-6 space-y-6">
        <animated.div
          style={{
            opacity: typeOpacity,
          }}
        >
          <ListContainer gapSize="sm">
            <ItemList
              defaultValue={selectedState.glass?._id}
              onChange={handleChange}
              items={configuration.glass.map((glass) => {
                return {
                  id: glass._id,
                  title: getItemTitle(
                    frameBuilderStrings,
                    glass.title ?? '',
                    glass.discountPercent,
                  ),
                  image: glass.icon,
                  description: selectedState.glass &&
                    selectedState.glass._id !== glass._id && (
                      <>
                        <PriceBadge
                          price={
                            framePricesWithDiscount[glass._id] -
                            framePricesWithDiscount[selectedState.glass._id]
                          }
                          showPlusSign={true}
                        />
                        {glass.discountPercent && (
                          <PriceBadge
                            price={
                              framePrices[glass._id] -
                              framePricesWithDiscount[selectedState.glass._id]
                            }
                            showPlusSign={true}
                            disabled
                            className="ml-1"
                          />
                        )}
                      </>
                    ),
                  details: (
                    <div className="w-full border-t border-gray-300 pt-3 pb-4 px-4">
                      {glass.description && (
                        <div className="flex text-xs leading-4">
                          {glass.description}
                        </div>
                      )}
                    </div>
                  ),
                }
              })}
            />
          </ListContainer>
        </animated.div>

        <div className="flex justify-between mt-6">
          <Button
            size="small"
            isVisuallyDisabled={!isValidSelection}
            onClick={handleOkClick}
            className="w-[49%] grow-0 shrink-0"
          >
            {frameBuilderStrings.controlOkLabel}
          </Button>
        </div>
      </div>

      {frameBuilderStrings.glassStepFooter && (
        <>
          <hr className="bg-gray-300" />
          <div className="flex flex-col px-6 space-y-6">
            <PortableText content={frameBuilderStrings.glassStepFooter} />
          </div>
        </>
      )}
    </ConfigurationDefinition>
  )
}

export default GlassDefinition
